import React from 'react';
import { connect } from 'react-redux';
import { Box, FullScreenSheet, Icon, Typography } from '@drone-js/core';
import { mapDispatchToProps, mapStateToProps, TextSearchOverlayProps } from 'component/common/TextSearch/TextSearchOverlay.selectors';
import { Category, SuggestionResult } from 'core/type/SuggestionResult.types';
import { OverlayTextSearchInput } from 'component/common/TextSearch/OverlayTextSearchInput';
import PageContainer from 'component/common/PageContainer/PageContainer';
import TextSearchResults from 'component/common/TextSearch/TextSearchResult/TextSearchResults';
import { BootstrapConfig, Geo } from 'core/type/BootstrapConfig.types';
import RecentSearch from 'component/common/TextSearch/TextSearchResult/RecentSearch/RecentSearch';
import { CustomSheet } from 'component/common/CustomSheet/CustomSheet';
import InCitySearchKeyword from 'component/common/InCitySearchKeyword/InCitySearchKeyword';
import InCitySearchTopActivities from 'component/common/InCitySearchTopActivities/InCitySearchTopActivities';
import NearMe from 'component/common/TextSearch/TextSearchResult/NearMe/NearMe';
import { MAX_RECENT_SEARCH, setRecentlySearch, transformSuggestionToRecentSearch } from 'component/common/TextSearch/suggestionUtils';
import Feature from 'component/common/Experiment/Feature';
import { FeatureName } from 'core/constant/FeatureName';
import { SearchEntryPointType } from 'component/common/SearchKeywordEntryPoint/SearchKeywordEntryPoint.selectors';
import SearchKeywordEntryPoint from 'component/common/SearchKeywordEntryPoint/SearchKeywordEntryPoint';
import { useSuggestions } from 'component/common/TextSearch/hooks/useSuggestions';

const TextSearchSearchBox: React.FC<{
    config: BootstrapConfig;
    isTextSearchOverlayVisible: boolean;
    searchText: string;
    setSearchText: React.Dispatch<React.SetStateAction<string>>;
    isGlobalKeywordSearch?: boolean;
    isModifyEnterKeyPressForSearchExpOn: boolean;
    onKeywordResultSelect?: (keyword: string, geo: Geo) => void;
    onGlobalKeywordResultSelect?: (keyword: string) => void;
    isDisabledGlobalKeywordSearch?: boolean;
}> = (props) => {
    return (
        <OverlayTextSearchInput
            isMockData={props.config.isMockData}
            isTextSearchOverlayVisible={props.isTextSearchOverlayVisible}
            searchText={props.searchText}
            setSearchText={props.setSearchText}
            isGlobalKeywordSearch={props.isGlobalKeywordSearch}
            isModifyEnterKeyPressForSearchExpOn={props.isModifyEnterKeyPressForSearchExpOn}
            onKeywordResultSelect={props.onKeywordResultSelect}
            onGlobalKeywordResultSelect={props.onGlobalKeywordResultSelect}
            isDisabledGlobalKeywordSearch={props.isDisabledGlobalKeywordSearch}
        />
    );
};
TextSearchSearchBox.displayName = 'TextSearchSearchBox';

const TextSearchOverlay: React.FC<TextSearchOverlayProps> = (props) => {
    const [searchText, setSearchText] = React.useState<string>(() => (props.isInCitySearch ? '' : props.searchText));

    React.useEffect(() => {
        if (!props.isInCitySearch) {
            setSearchText(props.searchText);
        }
    }, [props.isInCitySearch, props.searchText]);

    const { areSuggestionsLoading, suggestions, hasSuggestions } = useSuggestions(searchText);
    const updateSearchText = (text: string, selected?: SuggestionResult, category?: Category) => {
        setSearchText(text.substring(0, 100));
        props.setSearchText(text);
        selected && props.setSelectedResult(selected, category);
    };

    const handleCloseOverlay = () => {
        if (searchText !== props.selectedResult?.name) {
            updateSearchText('', undefined);
        }
        props.closeTextSearchOverlay();
        props.onClose && props.onClose();
    };

    const handleBackClickOverlay = () => {
        if (searchText !== props.selectedResult?.name) {
            updateSearchText('', undefined);
        }
        props.onClickBackTextSearchOverlay();
        props.onClose && props.onClose();
    };

    const onTextSearchSelect = (selected: SuggestionResult, category?: Category) => {
        // updateSearchText must be called after closeTextSearchOverlay
        // Otherwise user will be unable to navigate to the search page for new city
        props.trackTextSearchSelect(selected, searchText);
        props.closeTextSearchOverlay();
        const name = selected.name || selected.nameTranslated;
        const recentSearch = transformSuggestionToRecentSearch(selected);
        props.isSearchBoxFeatureGroup && setRecentlySearch(recentSearch, MAX_RECENT_SEARCH);
        updateSearchText(name, selected, category);
        props.onClose && props.onClose();
    };

    const recentSearchFeature = props.isSearchBoxFeatureGroup && props.isTextSearchOverlayVisible;
    const shouldHideRecentSearch = props.isHideRecentSearch && searchText !== '';

    return (
        <>
            {!props.isSearchBoxFeatureGroup ? (
                <CustomSheet onClose={handleCloseOverlay} data-testid="overlay-panel" visible={props.isTextSearchOverlayVisible} zIndex={1060}>
                    <PageContainer backgroundColor="base.surface">
                        {props.isTextSearchOverlayVisible && (
                            <Box p="12" pb="0" mb="0" maxWidth="744px" mx="auto" width="100%">
                                <TextSearchSearchBox
                                    config={props.config}
                                    isTextSearchOverlayVisible={props.isTextSearchOverlayVisible}
                                    searchText={searchText}
                                    setSearchText={setSearchText}
                                    isGlobalKeywordSearch={props.isGlobalKeywordSearch}
                                    onKeywordResultSelect={props.onKeywordResultSelect}
                                    onGlobalKeywordResultSelect={props.onGlobalKeywordResultSelect}
                                    isDisabledGlobalKeywordSearch={props.isDisabledGlobalKeywordSearch}
                                    isModifyEnterKeyPressForSearchExpOn={props.isModifyEnterKeyPressForSearchExpOn}
                                />
                            </Box>
                        )}
                        {props.isNearByEnabled && !searchText.length && <NearMe handleSelectedResult={onTextSearchSelect} />}
                        <Feature.On name={FeatureName.GlobalKeywordSearch}>
                            {props.isInCitySearch && hasSuggestions && (
                                <SearchKeywordEntryPoint searchType={SearchEntryPointType.InCity} inputText={searchText} cms={props.config.cms[316646]} />
                            )}
                        </Feature.On>
                        <Feature.Off name={FeatureName.GlobalKeywordSearch}>{props.isInCitySearch && <InCitySearchKeyword inputText={searchText} />}</Feature.Off>
                        {props.isInCitySearch && <InCitySearchTopActivities inputText={searchText} popularActivities={props.popularActivities} />}
                        {props.isTextSearchOverlayVisible && (
                            <TextSearchResults
                                searchText={searchText}
                                suggestions={suggestions}
                                areSuggestionsLoading={areSuggestionsLoading}
                                onSelect={onTextSearchSelect}
                                popularActivities={props.popularActivities}
                            />
                        )}
                        <Feature.On name={FeatureName.GlobalKeywordSearch}>
                            {props.isGlobalKeywordSearch && hasSuggestions && (
                                <SearchKeywordEntryPoint searchType={SearchEntryPointType.Global} inputText={searchText} cms={props.config.cms[360387]} />
                            )}
                        </Feature.On>
                    </PageContainer>
                </CustomSheet>
            ) : (
                <FullScreenSheet visible={props.isTextSearchOverlayVisible} onClose={handleCloseOverlay} zIndex={1060}>
                    <Box display="flex" position="sticky" top="0" backgroundColor="base.surface" flexDirection="column" boxShadow="bottom-s" px="16" pb="12" zIndex={1070}>
                        <Box display="flex" alignItems="center" flexDirection="row" mt="16" mb="8" mx="8">
                            <Box
                                data-element-name="activities-custom-sheet-close-button"
                                data-testid="activities-custom-sheet-close-button"
                                as="button"
                                width="fit-content"
                                onClick={handleBackClickOverlay}
                                p="0"
                                pe="12"
                            >
                                <Icon id="fill.arrow.thin-left" size="20" label={props.config.cms[130923]} />
                            </Box>
                            <Typography variant="heading-default">{props.config.cms[126799]}</Typography>
                        </Box>
                        <Box pt="16" width="100%" mx="auto">
                            <TextSearchSearchBox
                                config={props.config}
                                isTextSearchOverlayVisible={props.isTextSearchOverlayVisible}
                                searchText={searchText}
                                setSearchText={setSearchText}
                                isGlobalKeywordSearch={props.isGlobalKeywordSearch}
                                onKeywordResultSelect={props.onKeywordResultSelect}
                                onGlobalKeywordResultSelect={props.onGlobalKeywordResultSelect}
                                isDisabledGlobalKeywordSearch={props.isDisabledGlobalKeywordSearch}
                                isModifyEnterKeyPressForSearchExpOn={props.isModifyEnterKeyPressForSearchExpOn}
                            />
                        </Box>
                    </Box>
                    <PageContainer backgroundColor="base.surface" pt="12">
                        {props.isNearByEnabled && searchText === '' && <NearMe handleSelectedResult={onTextSearchSelect} />}
                        <Feature.On name={FeatureName.GlobalKeywordSearch}>
                            {props.isInCitySearch && hasSuggestions && (
                                <SearchKeywordEntryPoint searchType={SearchEntryPointType.InCity} inputText={searchText} cms={props.config.cms[316646]} />
                            )}
                        </Feature.On>
                        <Feature.Off name={FeatureName.GlobalKeywordSearch}>{props.isInCitySearch && <InCitySearchKeyword inputText={searchText} />}</Feature.Off>
                        {recentSearchFeature && !shouldHideRecentSearch && (
                            <div data-element-name="recent-search-section" data-testid="recent-search-section">
                                <RecentSearch />
                            </div>
                        )}
                        {props.isInCitySearch && <InCitySearchTopActivities inputText={searchText} popularActivities={props.popularActivities} />}
                        {props.isTextSearchOverlayVisible && (
                            <TextSearchResults
                                searchText={searchText}
                                suggestions={suggestions}
                                areSuggestionsLoading={areSuggestionsLoading}
                                onSelect={onTextSearchSelect}
                                popularActivities={props.popularActivities}
                            />
                        )}
                        <Feature.On name={FeatureName.GlobalKeywordSearch}>
                            {props.isGlobalKeywordSearch && hasSuggestions && (
                                <SearchKeywordEntryPoint searchType={SearchEntryPointType.Global} inputText={searchText} cms={props.config.cms[360387]} />
                            )}
                        </Feature.On>
                    </PageContainer>
                </FullScreenSheet>
            )}
        </>
    );
};
TextSearchOverlay.displayName = 'TextSearchOverlay';

export default connect(mapStateToProps, mapDispatchToProps)(TextSearchOverlay);
